"use client";

import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { trackEvent } from "@/lib/track";
import { Link } from "@/i18n/routing";
import FallbackImage from "@/components/ui/FallbackImage";
import {
  Minus,
  Plus,
  ShoppingBag,
  Calendar,
  Share2,
  Copy,
  Check,
  Mail,
  MessageCircle,
  AlertTriangle,
  Truck,
  Heart,
  Sparkles,
  Eye,
  Feather,
  Ruler,
  HelpCircle,
  Play,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatPrice } from "@/lib/utils";
import { useCart } from "@/context/CartContext";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/Button";
import { useLocale, useTranslations } from "next-intl";
import type { ProductDetail as ProductDetailType, ProductVariantData } from "@/lib/sample-data";
import BackInStockForm from "@/components/product/BackInStockForm";
import StickyAddToCart from "@/components/product/StickyAddToCart";
import InProductionBuilds from "@/components/product/InProductionBuilds";
import ProductReviews from "@/components/product/ProductReviews";
import ProductQuestions from "@/components/product/ProductQuestions";

/* ---------- Video detection helper ---------- */

const VIDEO_EXTENSIONS = [".mp4", ".mov", ".webm", ".avi", ".mkv"];

function isVideoUrl(url: string): boolean {
  const lower = url.toLowerCase().split("?")[0];
  return VIDEO_EXTENSIONS.some((ext) => lower.endsWith(ext)) || lower.includes("product-videos/");
}

/* ---------- Image Gallery with Zoom + Video ---------- */

function ImageGallery({
  images,
  imageAlts,
  mediaTypes,
  name,
  zoomLabel,
  productId,
}: {
  images: string[];
  imageAlts?: (string | null)[];
  mediaTypes?: string[]; // optional parallel array: "image" | "video"
  name: string;
  zoomLabel: string;
  productId: string;
}) {
  const getAlt = (idx: number) => imageAlts?.[idx] || `${name} by Rivky Wigs - view ${idx + 1}`;
  const [activeIndex, setActiveIndex] = useState(0);
  const [previousIndex, setPreviousIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const [isZooming, setIsZooming] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Placeholder images when none provided
  const displayImages =
    images.length > 0
      ? images
      : [null, null, null, null];

  // Determine media type for each item
  const getMediaType = useCallback((idx: number): "image" | "video" => {
    if (mediaTypes && mediaTypes[idx]) return mediaTypes[idx] as "image" | "video";
    const url = displayImages[idx];
    if (url && isVideoUrl(url)) return "video";
    return "image";
  }, [mediaTypes, displayImages]);

  const activeIsVideo = displayImages[activeIndex] ? getMediaType(activeIndex) === "video" : false;

  const handleImageChange = useCallback((newIndex: number) => {
    if (newIndex === activeIndex) return;
    setPreviousIndex(activeIndex);
    setIsFading(true);
    // Start crossfade
    setTimeout(() => {
      setActiveIndex(newIndex);
      setTimeout(() => setIsFading(false), 500);
    }, 50);
  }, [activeIndex]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!imageContainerRef.current) return;
      const rect = imageContainerRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setZoomPosition({ x, y });
    },
    []
  );

  return (
    <div className="flex flex-col gap-5">
      {/* Main Image/Video with Zoom + Crossfade */}
      <div
        ref={imageContainerRef}
        className={cn(
          "relative aspect-square md:aspect-[4/5] w-full overflow-hidden rounded-sm bg-white",
          !activeIsVideo && "cursor-crosshair"
        )}
        onMouseEnter={() => {
          if (!activeIsVideo) {
            setIsZooming(true);
            trackEvent("product_image_zoom", { productId });
          }
        }}
        onMouseLeave={() => {
          if (!activeIsVideo) setIsZooming(false);
        }}
        onMouseMove={!activeIsVideo ? handleMouseMove : undefined}
      >
        {/* Previous image (fades out during transition) — only for images */}
        {isFading && displayImages[previousIndex] && getMediaType(previousIndex) === "image" && (
          <FallbackImage
            src={displayImages[previousIndex]!}
            alt={getAlt(previousIndex)}
            fill
            sizes="(max-width: 768px) 100vw, 50vw"
            className="object-contain transition-opacity duration-500 ease-out opacity-0"
            style={{ zIndex: 1 }}
            fallbackClassName="absolute inset-0 bg-gradient-to-br from-brand-gold-light/30 via-brand-cream to-brand-gold/20"
          />
        )}

        {/* Active media */}
        {displayImages[activeIndex] ? (
          activeIsVideo ? (
            <video
              ref={videoRef}
              src={displayImages[activeIndex]!}
              controls
              playsInline
              className={cn(
                "absolute inset-0 w-full h-full object-contain bg-black",
                isFading ? "animate-fade-in" : ""
              )}
            />
          ) : (
            <FallbackImage
              src={displayImages[activeIndex]!}
              alt={getAlt(activeIndex)}
              fill
              sizes="(max-width: 768px) 100vw, 50vw"
              className={cn(
                "object-contain transition-all ease-out will-change-transform",
                isFading ? "duration-500 animate-fade-in" : "duration-500",
                isZooming && "scale-[2] duration-300"
              )}
              style={
                isZooming
                  ? {
                      transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`,
                    }
                  : undefined
              }
              priority
              fallbackClassName="absolute inset-0 bg-gradient-to-br from-brand-gold-light/30 via-brand-cream to-brand-gold/20"
            />
          )
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-brand-gold-light/30 via-brand-cream to-brand-gold/20" />
        )}

        {/* Zoom hint — only for images */}
        {displayImages[activeIndex] && !isZooming && !activeIsVideo && (
          <div className="absolute bottom-4 end-4 rounded-sm bg-brand-dark/60 px-3 py-1.5 text-[10px] tracking-wider uppercase text-white/90 backdrop-blur-sm pointer-events-none hidden md:block transition-opacity duration-500">
            {zoomLabel}
          </div>
        )}
      </div>

      {/* Thumbnails — scrollable on mobile */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
        {displayImages.map((img, idx) => {
          const isVideo = img ? getMediaType(idx) === "video" : false;
          return (
            <button
              key={idx}
              onClick={() => handleImageChange(idx)}
              className={cn(
                "relative aspect-square w-16 sm:w-20 shrink-0 overflow-hidden rounded-sm transition-all duration-500 ease-out",
                idx === activeIndex
                  ? "border-2 border-brand-gold shadow-sm"
                  : "border-2 border-transparent hover:border-brand-gold-light hover:scale-[1.04] hover:shadow-sm"
              )}
            >
              {img ? (
                isVideo ? (
                  <>
                    <video
                      src={img}
                      muted
                      playsInline
                      preload="metadata"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                      <div className="w-7 h-7 bg-white/90 rounded-full flex items-center justify-center shadow-sm">
                        <Play className="w-3.5 h-3.5 text-brand-dark ms-0.5" fill="currentColor" />
                      </div>
                    </div>
                  </>
                ) : (
                  <FallbackImage
                    src={img}
                    alt={getAlt(idx)}
                    fill
                    sizes="88px"
                    className="object-cover"
                    fallbackClassName="absolute inset-0 bg-gradient-to-br from-brand-gold-light/20 via-brand-cream to-brand-gold/10"
                  />
                )
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-brand-gold-light/20 via-brand-cream to-brand-gold/10" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Color Swatches ---------- */

function ColorSwatches({
  colors,
  selected,
  onSelect,
  label,
}: {
  colors: { name: string; hex: string }[];
  selected: string;
  onSelect: (name: string) => void;
  label: string;
}) {
  if (colors.length === 0) return null;

  return (
    <div>
      <p className="mb-3 text-[10px] font-medium uppercase tracking-[0.2em] text-brand-muted">
        {label}:{" "}
        <span className="font-normal normal-case tracking-normal text-brand-dark">{selected}</span>
      </p>
      <div className="flex gap-3" role="group" aria-label={label}>
        {colors.map((color) => (
          <button
            key={color.name}
            onClick={() => onSelect(color.name)}
            title={color.name}
            aria-label={color.name}
            aria-pressed={selected === color.name}
            className={cn(
              "min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full transition-all duration-500 ease-out",
              selected === color.name ? "scale-110" : "hover:scale-105"
            )}
          >
            <span
              className={cn(
                "block h-11 w-11 rounded-full border-2 transition-all duration-500 ease-out",
                selected === color.name
                  ? "border-brand-gold ring-2 ring-brand-gold/30 ring-offset-2"
                  : "border-gray-200 hover:border-brand-gold-light"
              )}
              style={{ backgroundColor: color.hex }}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---------- Quantity Selector ---------- */

function QuantitySelector({
  quantity,
  onChange,
  label,
  decreaseLabel,
  increaseLabel,
  max,
}: {
  quantity: number;
  onChange: (q: number) => void;
  label: string;
  decreaseLabel: string;
  increaseLabel: string;
  max?: number;
}) {
  const atMax = max != null && quantity >= max;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center">
        <p className="me-5 text-[10px] font-medium uppercase tracking-[0.2em] text-brand-muted">
          {label}
        </p>
        <div className="flex items-center border border-brand-dark/15 rounded-sm">
          <button
            onClick={() => onChange(Math.max(1, quantity - 1))}
            className="flex h-11 w-11 items-center justify-center text-brand-dark transition-all duration-500 hover:bg-brand-cream active:scale-[0.92] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/50 focus-visible:ring-inset"
            aria-label={decreaseLabel}
          >
            <Minus className="h-3.5 w-3.5" />
          </button>
          <span className="flex h-11 w-14 items-center justify-center border-x border-brand-dark/15 text-sm font-medium text-brand-dark tabular-nums">
            {quantity}
          </span>
          <button
            onClick={() => !atMax && onChange(quantity + 1)}
            disabled={atMax}
            className="flex h-11 w-11 items-center justify-center text-brand-dark transition-all duration-500 hover:bg-brand-cream active:scale-[0.92] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/50 focus-visible:ring-inset disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label={increaseLabel}
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {atMax && max != null && (
        <p className="text-[11px] text-amber-600 ms-[5.5rem]">
          Only {max} available
        </p>
      )}
    </div>
  );
}

/* ---------- Share Buttons ---------- */

function ShareButtons({ productName, productId }: { productName: string; productId: string }) {
  const t = useTranslations("product");
  const [copied, setCopied] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const currentUrl = typeof window !== "undefined" ? window.location.href : "";

  const handleShare = useCallback(async () => {
    // Try Web Share API first (mobile-friendly)
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title: productName,
          url: currentUrl,
        });
        return;
      } catch {
        // User cancelled or API not available -- fall through to dropdown
      }
    }
    // Fallback: toggle dropdown
    setShowDropdown((prev) => !prev);
  }, [productName, currentUrl]);

  const handleCopyLink = useCallback(async () => {
    trackEvent("product_share_click", { method: "clipboard", productId });
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = currentUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
    setShowDropdown(false);
  }, [currentUrl]);

  const handleWhatsApp = useCallback(() => {
    trackEvent("product_share_click", { method: "whatsapp", productId });
    const text = encodeURIComponent(`${productName}\n${currentUrl}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
    setShowDropdown(false);
  }, [productName, currentUrl, productId]);

  const handleEmail = useCallback(() => {
    trackEvent("product_share_click", { method: "email", productId });
    const subject = encodeURIComponent(productName);
    const body = encodeURIComponent(`Check out this product: ${currentUrl}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    setShowDropdown(false);
  }, [productName, currentUrl, productId]);

  return (
    <div className="relative">
      <button
        onClick={handleShare}
        className="flex items-center gap-1.5 text-xs text-brand-muted transition-colors hover:text-brand-dark"
        aria-label={t("shareProduct")}
      >
        <Share2 className="h-4 w-4" />
        {t("shareProduct")}
      </button>

      {/* Dropdown fallback for desktop */}
      {showDropdown && (
        <>
          {/* Backdrop to close dropdown */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowDropdown(false)}
          />
          <div className="absolute end-0 top-full z-50 mt-2 w-52 rounded-lg border border-brand-dark/10 bg-white py-1 shadow-lg">
            <button
              onClick={handleCopyLink}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-brand-dark transition-colors hover:bg-brand-cream"
            >
              {copied ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
              {copied ? t("linkCopied") : t("copyLink")}
            </button>
            <button
              onClick={handleWhatsApp}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-brand-dark transition-colors hover:bg-brand-cream"
            >
              <MessageCircle className="h-4 w-4" />
              {t("shareViaWhatsApp")}
            </button>
            <button
              onClick={handleEmail}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-brand-dark transition-colors hover:bg-brand-cream"
            >
              <Mail className="h-4 w-4" />
              {t("shareViaEmail")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* ---------- Product Tabs ---------- */

type TabId = "description" | "specifications" | "reviews";

function ProductTabs({
  description,
  specs,
  productId,
}: {
  description: string;
  specs: Record<string, string>;
  productId: string;
}) {
  const t = useTranslations("product");
  const [activeTab, setActiveTab] = useState<TabId>("description");

  const tabs: { id: TabId; label: string }[] = [
    { id: "description", label: t("description") },
    { id: "specifications", label: t("specifications") },
    { id: "reviews", label: t("reviews") },
  ];

  const specsEntries = Object.entries(specs);

  return (
    <div className="mt-10 border-t border-brand-dark/8 pt-8">
      {/* Tab Headers */}
      <div className="flex overflow-x-auto border-b border-brand-dark/8 -mx-1 px-1" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`tabpanel-${tab.id}`}
            onClick={() => { setActiveTab(tab.id); trackEvent("product_tab_click", { tab: tab.id, productId }); }}
            className={cn(
              "relative px-3 sm:px-5 py-3.5 text-[11px] sm:text-xs font-medium uppercase tracking-[0.1em] sm:tracking-[0.15em] transition-colors duration-500 whitespace-nowrap flex-shrink-0 min-h-[44px]",
              activeTab === tab.id
                ? "text-brand-dark"
                : "text-brand-muted hover:text-brand-dark"
            )}
          >
            {tab.label}
            <span
              className={cn(
                "absolute inset-x-0 bottom-0 h-[2px] bg-brand-gold transition-all duration-500 ease-out",
                activeTab === tab.id ? "opacity-100 scale-x-100" : "opacity-0 scale-x-0"
              )}
            />
          </button>
        ))}
      </div>

      {/* Tab Panels */}
      <div className="py-8">
        {/* Description */}
        {activeTab === "description" && (
          <div
            id="tabpanel-description"
            role="tabpanel"
            className="animate-fade-up-fill"
          >
            <p className="max-w-xl text-sm leading-relaxed text-brand-dark/80 sm:text-base sm:leading-7">
              {description}
            </p>
          </div>
        )}

        {/* Specifications */}
        {activeTab === "specifications" && (
          <div
            id="tabpanel-specifications"
            role="tabpanel"
            className="animate-fade-up-fill"
          >
            {specsEntries.length > 0 ? (
              <div className="overflow-x-auto -mx-2 px-2">
              <table className="w-full max-w-xl">
                <tbody>
                  {specsEntries.map(([key, value], idx) => (
                    <tr
                      key={key}
                      className={cn(
                        "border-b border-brand-dark/5",
                        idx % 2 === 0 ? "bg-brand-cream/30" : ""
                      )}
                    >
                      <td className="px-4 py-3.5 text-[11px] font-medium uppercase tracking-wider text-brand-muted">
                        {key}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-brand-dark">
                        {value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            ) : (
              <p className="text-sm text-brand-muted">
                {t("noSpecsAvailable")}
              </p>
            )}
          </div>
        )}

        {/* Reviews */}
        {activeTab === "reviews" && (
          <div
            id="tabpanel-reviews"
            role="tabpanel"
            className="animate-fade-up-fill"
          >
            <ProductReviews productId={productId} />
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Variant Selector ---------- */

function VariantSelector({
  variants,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  selectedVariantId,
  onSelect,
}: {
  variants: ProductVariantData[];
  selectedVariantId: string | null;
  onSelect: (variantId: string | null) => void;
}) {
  // Extract unique option keys from all variants (exclude internal keys)
  const INTERNAL_KEYS = new Set(["imageStartIdx", "imageEndIdx"]);
  const optionKeys = useMemo(() => {
    const keys = new Set<string>();
    variants.forEach((v) => {
      if (v.options && typeof v.options === "object") {
        Object.keys(v.options).forEach((k) => {
          if (!INTERNAL_KEYS.has(k)) keys.add(k);
        });
      }
    });
    return Array.from(keys);
  }, [variants]);

  // Track selected value per option key
  const [selections, setSelections] = useState<Record<string, string>>(() => {
    // Initialize with the first variant's options
    if (variants.length > 0 && variants[0].options) {
      return { ...variants[0].options };
    }
    return {};
  });

  // Get available values per option key
  const optionValues = useMemo(() => {
    const result: Record<string, string[]> = {};
    optionKeys.forEach((key) => {
      const values = new Set<string>();
      variants.forEach((v) => {
        if (v.options && v.options[key]) {
          values.add(v.options[key]);
        }
      });
      result[key] = Array.from(values);
    });
    return result;
  }, [variants, optionKeys]);

  // Find the matching variant based on current selections
  useEffect(() => {
    const match = variants.find((v) => {
      if (!v.options) return false;
      return optionKeys.every((key) => v.options[key] === selections[key]);
    });
    onSelect(match?.id ?? null);
  }, [selections, variants, optionKeys, onSelect]);

  const handleOptionChange = useCallback((key: string, value: string) => {
    setSelections((prev) => ({ ...prev, [key]: value }));
  }, []);

  if (optionKeys.length === 0) return null;

  return (
    <div className="space-y-5">
      {optionKeys.map((key) => (
        <div key={key}>
          <p className="mb-3 text-[10px] font-medium uppercase tracking-[0.2em] text-brand-muted">
            {key}:{" "}
            <span className="font-normal normal-case tracking-normal text-brand-dark">
              {selections[key] || ""}
            </span>
          </p>
          <div className="flex flex-wrap gap-2.5">
            {optionValues[key]?.map((value) => (
              <button
                key={value}
                onClick={() => handleOptionChange(key, value)}
                className={cn(
                  "rounded-sm border px-5 py-2.5 text-sm font-medium transition-all duration-500 ease-out",
                  selections[key] === value
                    ? "border-brand-gold bg-brand-gold/8 text-brand-dark shadow-sm"
                    : "border-brand-dark/12 text-brand-muted hover:border-brand-gold-light hover:text-brand-dark"
                )}
              >
                {value}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Main Component ---------- */

export interface ProductDetailProps {
  product: ProductDetailType;
}

export default function ProductDetail({ product }: ProductDetailProps) {
  const { addItem } = useCart();
  const { toast } = useToast();
  const t = useTranslations("product");
  const tCommon = useTranslations("common");
  const tc = useTranslations("consultation");
  const locale = useLocale();
  const [selectedColor, setSelectedColor] = useState(
    product.colors[0]?.name ?? ""
  );
  const [quantity, setQuantity] = useState(1);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);

  // Track product view
  useEffect(() => {
    trackEvent("product_view", {
      productId: product.id,
      name: product.name,
      price: product.price,
      category: product.category,
    });
  }, [product.id, product.name, product.price, product.category]);

  // Cross-sell accessories
  const [crossSellProducts, setCrossSellProducts] = useState<
    { id: string; name: string; slug: string; price: number; image: string | null }[]
  >([]);
  useEffect(() => {
    fetch("/api/products?category=accessories&limit=3")
      .then((res) => (res.ok ? res.json() : { products: [] }))
      .then((data) => {
        if (data.products && data.products.length > 0) {
          setCrossSellProducts(data.products.slice(0, 3));
        }
      })
      .catch(() => {});
  }, []);

  const hasVariants = product.variants && product.variants.length > 0;

  // Filter images based on selected variant (if variant has imageStartIdx/imageEndIdx)
  const variantImages = useMemo(() => {
    if (!selectedVariantId || !hasVariants) return product.images;
    const variant = product.variants!.find((v) => v.id === selectedVariantId);
    const opts = variant?.options as Record<string, unknown> | undefined;
    if (!opts) return product.images;
    const startIdx = opts.imageStartIdx != null ? Number(opts.imageStartIdx) : undefined;
    const endIdx = opts.imageEndIdx != null ? Number(opts.imageEndIdx) : undefined;
    if (startIdx !== undefined && endIdx !== undefined && !isNaN(startIdx) && !isNaN(endIdx) && product.images.length > startIdx) {
      return product.images.slice(startIdx, Math.min(endIdx + 1, product.images.length));
    }
    return product.images;
  }, [selectedVariantId, hasVariants, product.variants, product.images]);

  const variantImageAlts = useMemo(() => {
    if (!product.imageAlts) return undefined;
    if (!selectedVariantId || !hasVariants) return product.imageAlts;
    const variant = product.variants!.find((v) => v.id === selectedVariantId);
    const opts = variant?.options as Record<string, unknown> | undefined;
    if (!opts) return product.imageAlts;
    const startIdx = opts.imageStartIdx != null ? Number(opts.imageStartIdx) : undefined;
    const endIdx = opts.imageEndIdx != null ? Number(opts.imageEndIdx) : undefined;
    if (startIdx !== undefined && endIdx !== undefined && !isNaN(startIdx) && !isNaN(endIdx)) {
      return product.imageAlts.slice(startIdx, Math.min(endIdx + 1, product.imageAlts.length));
    }
    return product.imageAlts;
  }, [selectedVariantId, hasVariants, product.variants, product.imageAlts]);

  // Find the selected variant
  const selectedVariant = useMemo(() => {
    if (!hasVariants || !selectedVariantId) return null;
    return product.variants!.find((v) => v.id === selectedVariantId) ?? null;
  }, [hasVariants, selectedVariantId, product.variants]);

  // Effective price/stock based on variant selection
  const effectivePrice = selectedVariant?.price != null
    ? selectedVariant.price
    : product.price;
  // Consignment availability: if the wig is at a reseller right now
  // but still visible to the public (per platform + reseller flags),
  // the product stays shoppable. The API pre-computes
  // `effectivelyInStock` which folds consignedAvailableQty into the
  // in-stock decision; we use it when present, else fall back.
  // Variant selection still wins (variant-level inStock is the truth
  // when a specific SKU is picked).
  const consignedQty =
    ((product as unknown as { consignmentAvailableQty?: number }).consignmentAvailableQty) || 0;
  const apiEffectiveInStock =
    (product as unknown as { effectivelyInStock?: boolean }).effectivelyInStock;
  const baseEffectiveInStock =
    apiEffectiveInStock !== undefined
      ? apiEffectiveInStock
      : product.inStock || consignedQty > 0;
  const effectiveInStock = selectedVariant != null
    ? selectedVariant.inStock
    : baseEffectiveInStock;
  const effectiveStockForQty = (product.stockCount || 0) + consignedQty;

  // Pre-order: when enabled, the product can be purchased even if stockCount
  // is zero or inStock is false. Stock checks and "Sold Out" messaging are
  // suppressed; the Add to Cart button switches to "Pre-order Now" copy and
  // a ship-date notice is shown above it.
  const isPreorder = !!product.preorderEnabled;
  // Explicit locale + UTC pin: `undefined` locale renders en-US on the
  // server but the visitor's locale in the browser (hydration mismatch,
  // React #418), and local-TZ formatting shifts the UTC-midnight-encoded
  // ship date back a day for anyone west of UTC.
  const preorderShipLabel = product.preorderShipDate
    ? new Date(product.preorderShipDate).toLocaleDateString(locale, {
        month: "long",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      })
    : null;

  const hasDiscount =
    product.comparePrice && product.comparePrice > effectivePrice;

  const handleAddToCart = useCallback(() => {
    // Stock check before add — suppressed for pre-order items, which are
    // expected to have zero stock by definition.
    if (!isPreorder) {
      const availableStock = selectedVariant?.stockCount ?? product.stockCount ?? Infinity;
      if (quantity > availableStock) {
        toast(`Only ${availableStock} available`, "error");
        return;
      }
    }

    const config: Record<string, string> = {};
    if (selectedColor) config.color = selectedColor;
    if (selectedVariantId) config.variantId = selectedVariantId;
    if (selectedVariant) {
      config.variantName = selectedVariant.name;
      if (selectedVariant.sku) config.variantSku = selectedVariant.sku;
    }

    addItem({
      productId: product.id,
      name: selectedVariant
        ? `${product.name} - ${selectedVariant.name}`
        : product.name,
      price: effectivePrice,
      quantity,
      image: product.image ?? undefined,
      slug: product.slug,
      categorySlug: product.category || undefined,
      customConfig: Object.keys(config).length > 0 ? config : undefined,
      isPreorder,
      preorderShipDate:
        isPreorder && product.preorderShipDate
          ? String(product.preorderShipDate)
          : undefined,
      // Carry the variant identity through to checkout so OrderItem can
      // reference it (drives variant-level stock decrement + admin
      // visibility).
      variantId: selectedVariant?.id,
      variantSku: selectedVariant?.sku ?? undefined,
    });
  }, [addItem, product, quantity, selectedColor, selectedVariantId, selectedVariant, effectivePrice, isPreorder, toast]);

  return (
    <div className="grid grid-cols-1 gap-10 md:gap-14 lg:grid-cols-2 lg:gap-16">
      {/* Left: Image Gallery */}
      <div className="animate-fade-up-fill" style={{ animationDelay: "0ms" }}>
        <ImageGallery images={variantImages} imageAlts={variantImageAlts} name={product.name} zoomLabel={t("hoverToZoom")} productId={product.id} />
      </div>

      {/* Right: Product Info */}
      <div className="flex flex-col lg:py-2">
        {/* Category label */}
        <div className="flex items-start justify-between gap-4 animate-fade-up-fill" style={{ animationDelay: "100ms" }}>
          <div>
            <p className="mb-2.5 text-[10px] font-medium uppercase tracking-[0.2em] text-brand-gold">
              {t("premiumCollection")}
            </p>
            <h1 className="font-display text-3xl tracking-tight text-brand-dark sm:text-4xl">
              {product.name}
            </h1>
            {/* Consignment brand — rendered only when the product came
                from an external supplier and a brand is on file. The
                `product.brand` field is populated at intake time from
                supplier.brandName (overridable per-product). */}
            {(product as { brand?: string | null }).brand && (
              <p className="mt-1.5 text-sm text-brand-muted">
                by <span className="font-medium text-brand-dark">{(product as { brand?: string | null }).brand}</span>
              </p>
            )}
          </div>
          <ShareButtons productName={product.name} productId={product.id} />
        </div>

        {/* Price */}
        <div className="mt-4 flex items-baseline gap-3 animate-fade-up-fill" style={{ animationDelay: "150ms" }}>
          <span className="font-display text-2xl text-brand-dark">
            {formatPrice(effectivePrice)}
          </span>
          {hasDiscount && (
            <span className="text-sm text-brand-muted line-through">
              {formatPrice(product.comparePrice!)}
            </span>
          )}
          {hasDiscount && (
            <span className="text-emerald-600 text-sm font-medium">
              {t("youSave", { amount: formatPrice(product.comparePrice! - effectivePrice), percent: Math.round(((product.comparePrice! - effectivePrice) / product.comparePrice!) * 100) })}
            </span>
          )}
        </div>

        {/* Sold Out -- enhanced messaging. Suppressed for pre-order items
            AND when consignment availability keeps the product shoppable. */}
        {!isPreorder && product.stockCount != null && product.stockCount === 0 && consignedQty === 0 && (
          <div className="mt-4 animate-fade-up-fill rounded-sm border border-brand-dark/5 bg-brand-cream/40 p-4">
            <p className="text-sm font-medium text-brand-dark mb-1">
              {t("currentlyUnavailable")}
            </p>
            <p className="text-xs text-brand-muted leading-relaxed">
              {t("unavailableDesc")}
            </p>
          </div>
        )}

        {/* Pre-order notice — replaces sold-out messaging when this product
            is being sold ahead of stock arrival. Soft gold treatment so it
            reads as an invitation rather than a warning. */}
        {isPreorder && (
          <div className="mt-4 animate-fade-up-fill rounded-sm border border-brand-gold/30 bg-brand-cream/60 p-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-brand-gold" />
              <p className="text-sm font-medium text-brand-dark">
                {t("preorderTitle")}
              </p>
            </div>
            <p className="text-xs text-brand-muted leading-relaxed mt-1.5">
              {preorderShipLabel
                ? t("preorderShipsOn", { date: preorderShipLabel })
                : t("preorderShipsLater")}
            </p>
          </div>
        )}

        {/* Low Stock Warning */}
        {product.stockCount != null && product.stockCount > 0 && product.stockCount <= 5 && (
          <div className="mt-3 animate-fade-up-fill">
            <span className="text-amber-600 text-sm font-medium flex items-center gap-1.5">
              <AlertTriangle className="h-4 w-4" />
              {t("lowStockWarning", { count: product.stockCount })} &mdash; {t("orderSoon")}
            </span>
          </div>
        )}

        {/* Free Shipping Progress */}
        {effectivePrice < 500 && (
          <div className="mt-3 animate-fade-up-fill">
            <div className="flex justify-between text-xs text-brand-muted mb-1">
              <span className="flex items-center gap-1"><Truck className="h-3.5 w-3.5" /> {t("freeShippingProgress")}</span>
              <span>{t("amountAway", { amount: formatPrice(500 - effectivePrice) })}</span>
            </div>
            <div className="h-1.5 bg-brand-dark/5 rounded-full overflow-hidden">
              <div className="h-full bg-brand-gold rounded-full transition-all duration-500" style={{ width: `${Math.min(100, (effectivePrice / 500) * 100)}%` }} />
            </div>
          </div>
        )}

        {/* Stock status for selected variant */}
        {hasVariants && selectedVariant && (
          <div className="mt-2.5 animate-fade-up-fill">
            {selectedVariant.inStock ? (
              <span className="text-[11px] font-medium text-green-600/90 tracking-wide">{t("inStockLabel")}</span>
            ) : (
              <span className="text-[11px] font-medium text-red-500/90 tracking-wide">{t("outOfStockLabel")}</span>
            )}
            {selectedVariant.sku && (
              <span className="ms-3 text-[11px] text-brand-muted tracking-wide">
                SKU: {selectedVariant.sku}
              </span>
            )}
          </div>
        )}

        {/* Short description */}
        <p className="mt-5 max-w-xl text-sm leading-relaxed text-brand-muted sm:text-base sm:leading-7 animate-fade-up-fill" style={{ animationDelay: "200ms" }}>
          {product.shortDescription}
        </p>

        {/* Divider */}
        <hr className="my-7 border-brand-dark/8" />

        {/* Variant Selector */}
        {hasVariants && (
          <div className="mb-7 animate-fade-up-fill" style={{ animationDelay: "250ms" }}>
            <VariantSelector
              variants={product.variants!}
              selectedVariantId={selectedVariantId}
              onSelect={setSelectedVariantId}
            />
          </div>
        )}

        {/* Colors (only show if no variants, since variants may already include color) */}
        {!hasVariants && product.colors.length > 0 && (
          <div className="mb-7 animate-fade-up-fill" style={{ animationDelay: "250ms" }}>
            <ColorSwatches
              colors={product.colors}
              selected={selectedColor}
              onSelect={setSelectedColor}
              label={t("color")}
            />
          </div>
        )}

        {/* Quantity */}
        <div className="mb-7 animate-fade-up-fill" style={{ animationDelay: "300ms" }}>
          <QuantitySelector
            quantity={quantity}
            onChange={setQuantity}
            label={t("quantity")}
            decreaseLabel={tCommon("decreaseQuantity")}
            increaseLabel={tCommon("increaseQuantity")}
            max={effectiveStockForQty || (product.stockCount ?? undefined)}
          />
        </div>

        {/* Add to Cart — pre-order items override stock-based disabled logic
            and switch to "Pre-order Now" copy */}
        <div className="animate-fade-up-fill" style={{ animationDelay: "350ms" }}>
          <Button
            variant="primary"
            size="lg"
            onClick={handleAddToCart}
            disabled={
              isPreorder
                ? hasVariants && !selectedVariantId
                : !effectiveInStock || (hasVariants && !selectedVariantId) || effectiveStockForQty === 0
            }
            className="w-full px-10 py-4 shadow-sm transition-all duration-500 hover:shadow-lg hover:scale-[1.01] active:scale-[0.98]"
            data-add-to-cart-main
          >
            <ShoppingBag className="h-4 w-4" />
            {isPreorder
              ? hasVariants && !selectedVariantId
                ? t("selectOptions")
                : t("preorderNow")
              : effectiveStockForQty === 0
                ? t("soldOut")
                : !effectiveInStock
                  ? t("soldOut")
                  : hasVariants && !selectedVariantId
                    ? t("selectOptions")
                    : t("addToCart")}
          </Button>
        </div>

        {/* In-production builds — speculative wigs admin has flipped
            listedForSale=true on. Customer can pre-order one of these
            specific physical wigs while it's being built. */}
        <InProductionBuilds
          productId={product.id}
          productName={product.name}
          productPrice={effectivePrice}
          productImage={product.image ?? undefined}
        />

        {/* Sticky Add to Cart -- mobile only */}
        <StickyAddToCart
          productName={product.name}
          price={effectivePrice}
          comparePrice={product.comparePrice}
          productId={product.id}
          image={product.image ?? undefined}
          inStock={effectiveInStock ?? true}
          stockCount={selectedVariant?.stockCount ?? product.stockCount}
        />

        {/* Back in Stock Notification + Browse Similar — hidden for
            pre-order items since they're already buyable */}
        {!effectiveInStock && !isPreorder && (
          <>
            <BackInStockForm productId={product.id} />
            {product.category && (
              <div className="mt-3 animate-fade-up-fill">
                <Button variant="secondary" size="sm" asChild className="w-full">
                  <Link href={`/${product.category}`}>
                    {t("browseSimilar")}
                  </Link>
                </Button>
              </div>
            )}
          </>
        )}

        {/* Book Consultation — wig-only. Accessories (brushes, serums,
            etc.) don't need a consultation to pick. */}
        {(["wigs", "toppers", "processed-wigs"].includes(product.category || "")) && (
        <div className="animate-fade-up-fill" style={{ animationDelay: "400ms" }}>
          <Button variant="secondary" size="lg" asChild className="mt-3 w-full transition-all duration-500 hover:shadow-md active:scale-[0.98]">
            <Link href="/book?service=new_wig">
              <Calendar className="h-4 w-4" />
              {tc("bookNow")}
            </Link>
          </Button>
        </div>
        )}

        {/* Tabs: Description | Specifications | Reviews */}
        <ProductTabs
          description={product.description}
          specs={product.specs}
          productId={product.id}
        />

        {/* Questions & Answers */}
        <div className="mt-10 border-t border-brand-dark/8 pt-8">
          <h3 className="mb-6 font-display text-lg tracking-tight text-brand-dark">
            Questions &amp; Answers
          </h3>
          <ProductQuestions productId={product.id} />
        </div>

        {/* Why You Will Love It + Care + Sizing — wigs/toppers only */}
        {(["wigs", "toppers", "processed-wigs"].includes(product.category || "")) && (<>
        <div className="mt-10 border-t border-brand-dark/8 pt-8">
          <h3 className="mb-6 font-display text-lg tracking-tight text-brand-dark">
            {t("whyYouWillLoveIt")}
          </h3>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {[
              { icon: Heart, title: t("trustHandcrafted"), desc: t("trustHandcraftedDesc") },
              { icon: Sparkles, title: t("trustPremiumHair"), desc: t("trustPremiumHairDesc") },
              { icon: Eye, title: t("trustNaturalLook"), desc: t("trustNaturalLookDesc") },
              { icon: Feather, title: t("trustComfort"), desc: t("trustComfortDesc") },
            ].map((item) => (
              <div key={item.title} className="flex gap-3.5">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-gold/8">
                  <item.icon className="h-4 w-4 text-brand-gold" />
                </div>
                <div>
                  <p className="text-sm font-medium text-brand-dark">{item.title}</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-brand-muted">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Care Instructions */}
        <div className="mt-10 border-t border-brand-dark/8 pt-8">
          <h3 className="mb-4 font-display text-lg tracking-tight text-brand-dark">
            {t("careInstructions")}
          </h3>
          <p className="mb-4 text-sm leading-relaxed text-brand-muted">
            {t("careIntro")}
          </p>
          <ul className="space-y-2.5">
            {[t("careTip1"), t("careTip2"), t("careTip3"), t("careTip4"), t("careTip5")].map((tip, idx) => (
              <li key={idx} className="flex items-start gap-2.5 text-sm text-brand-dark/80">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-gold" />
                {tip}
              </li>
            ))}
          </ul>
        </div>

        {/* Sizing Guide */}
        <div className="mt-10 border-t border-brand-dark/8 pt-8">
          <div className="flex items-center gap-2.5 mb-4">
            <Ruler className="h-5 w-5 text-brand-gold" />
            <h3 className="font-display text-lg tracking-tight text-brand-dark">
              {t("sizingGuide")}
            </h3>
          </div>
          <p className="mb-4 text-sm leading-relaxed text-brand-muted">
            {t("sizingGuideDesc")}
          </p>
          <div className="space-y-1.5">
            {[t("sizingPetite"), t("sizingAverage"), t("sizingLarge"), t("sizingCustom")].map((size, idx) => (
              <p key={idx} className="text-sm text-brand-dark/80">{size}</p>
            ))}
          </div>
        </div>

        </>)}

        {/* Need Help Choosing? — wig-only. Accessories (paddle brushes,
            hair products, etc.) don't need a consultation to pick. */}
        {(["wigs", "toppers", "processed-wigs"].includes(product.category || "")) && (
        <div className="mt-10 rounded-lg border border-brand-gold/20 bg-brand-cream/40 p-6 sm:p-8 text-center">
          <HelpCircle className="mx-auto mb-3 h-6 w-6 text-brand-gold" />
          <h3 className="font-display text-lg tracking-tight text-brand-dark">
            {t("needHelpChoosing")}
          </h3>
          <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-brand-muted">
            {t("needHelpChoosingDesc")}
          </p>
          <Button variant="primary" size="md" asChild className="mt-5 shadow-sm">
            <Link href="/book?service=new_wig">
              <Calendar className="h-4 w-4" />
              {t("bookFreeConsultation")}
            </Link>
          </Button>
        </div>
        )}

        {/* Complete the Look — Cross-sell Accessories */}
        {crossSellProducts.length > 0 && (
          <div className="mt-10 border-t border-brand-dark/8 pt-8">
            <p className="mb-1 text-[10px] font-medium uppercase tracking-[0.2em] text-brand-gold">
              {t("pairItWith")}
            </p>
            <h3 className="mb-5 font-display text-lg tracking-tight text-brand-dark">
              {t("completeTheLook")}
            </h3>
            <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory">
              {crossSellProducts.map((item) => (
                <div
                  key={item.id}
                  className="flex min-w-[180px] sm:min-w-[200px] flex-1 items-center gap-3 rounded-sm border border-brand-dark/8 p-3 transition-all duration-500 hover:border-brand-gold-light hover:shadow-sm snap-start"
                >
                  <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-sm bg-brand-cream">
                    {item.image ? (
                      <FallbackImage
                        src={item.image}
                        alt={`${item.name} - Accessory by Rivky Wigs`}
                        fill
                        sizes="64px"
                        className="object-cover"
                        fallbackClassName="absolute inset-0 bg-gradient-to-br from-brand-gold-light/20 via-brand-cream to-brand-gold/10"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-brand-gold-light/20 via-brand-cream to-brand-gold/10" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-brand-dark truncate">{item.name}</p>
                    <p className="text-xs text-brand-muted">{formatPrice(item.price)}</p>
                  </div>
                  <button
                    onClick={() => {
                      addItem({
                        productId: item.id,
                        name: item.name,
                        price: item.price,
                        quantity: 1,
                        image: item.image ?? undefined,
                      });
                    }}
                    className="shrink-0 rounded-sm border border-brand-gold bg-brand-gold/5 px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-brand-gold transition-all duration-500 hover:bg-brand-gold hover:text-white"
                  >
                    {tCommon("quickAdd")}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
